import { validDate } from "./peopleStatsData";

export const AREA_LABELS = {
  home_care: {
    Getting_Up_Sitting_Down: "Getting Up & Sitting Down",
    Bathroom_Help: "Bathroom Help",
    Walking_At_Home: "Walking at Home",
    Stairs_Entryway: "Stairs / Entryway",
    Steadiness_While_Moving: "Steadiness While Moving",
    Change_From_Usual: "Change From Usual",
  },
  senior_living: {
    Transfers: "Transfers",
    Bathroom_Routine: "Bathroom Routine",
    Walking_Around_Community: "Walking Around Community",
    Balance_Stability: "Balance & Stability",
    Activity_Change: "Activity Change",
  },
};
const requireValid = (condition) => {
  if (!condition) {
    const error = new Error("INVALID_RESPONSE");
    error.code = "INVALID_RESPONSE";
    throw error;
  }
};
const count = (value) => Number.isSafeInteger(value) && value >= 0;
const near = (a, b) =>
  typeof a === "number" && Number.isFinite(a) && Math.abs(a - b) < 0.00001;
const checkCounts = (value) => {
  requireValid(
    value &&
      [
        "high",
        "mid",
        "low",
        "no_data",
        "eligible_people",
        "scored_people",
      ].every((key) => count(value[key])),
  );
  requireValid(
    value.scored_people === value.high + value.mid + value.low &&
      value.eligible_people === value.scored_people + value.no_data,
  );
  requireValid(
    value.scored_people
      ? near(value.high_share_percent, (100 * value.high) / value.scored_people)
      : value.high_share_percent === null,
  );
};
const checkMovement = (value, current) => {
  requireValid(
    value &&
      value.eligible_current_people === current &&
      ["compared_people", "improved", "declined", "same"].every((key) =>
        count(value[key]),
      ),
  );
  requireValid(
    value.excluded &&
      ["no_previous_session", "unscored_pair", "history_unavailable"].every(
        (key) => count(value.excluded[key]),
      ),
  );
  requireValid(
    value.compared_people === value.improved + value.declined + value.same &&
      current ===
        value.compared_people +
          value.excluded.no_previous_session +
          value.excluded.unscored_pair +
          value.excluded.history_unavailable,
  );
};

// Fail closed on incompatible or inconsistent aggregates. Never rescore backend
// sessions or convert missing observations into zeroes in the renderer.
export function validatePopulationAggregate(
  data,
  { mode, filters, scoringVersion, timezone },
) {
  requireValid(
    data?.schema_version === "population-trend.v1" &&
      typeof data.snapshot_id === "string",
  );
  requireValid(
    data.account?.eligible === true &&
      data.account.mode === mode &&
      AREA_LABELS[mode],
  );
  if (!scoringVersion || data.scoring?.version !== scoringVersion) {
    const error = new Error("SCORING_VERSION_UNSUPPORTED");
    error.code = "SCORING_VERSION_UNSUPPORTED";
    throw error;
  }
  requireValid(
    validDate(data.as_of?.date) &&
      data.as_of.validity_days === 30 &&
      typeof data.as_of.timezone === "string",
  );
  try {
    new Intl.DateTimeFormat("en", { timeZone: data.as_of.timezone }).format();
  } catch {
    requireValid(false);
  }
  requireValid(!timezone || data.as_of.timezone === timezone);
  requireValid(
    data.scoring.session_policy_version === "canonical-local-day.v1",
  );
  requireValid(
    data.filters?.age_band === filters.age &&
      data.filters.gender === filters.sex.toLowerCase(),
  );
  requireValid(
    data.roster?.complete === true &&
      count(data.roster.cohort_total) &&
      count(data.roster.registered_total) &&
      data.roster.registered_total >= data.roster.cohort_total,
  );
  requireValid(
    data.coverage &&
      ["current", "overdue", "never_tested", "unavailable"].every((key) =>
        count(data.coverage[key]),
      ) &&
      ["current", "overdue", "never_tested", "unavailable"].reduce(
        (sum, key) => sum + data.coverage[key],
        0,
      ) === data.roster.cohort_total,
  );
  requireValid(
    data.quality &&
      ["complete", "partial"].includes(data.quality.state) &&
      typeof data.quality.history_complete === "boolean",
  );
  const quality = data.quality;
  requireValid(
    ["report_unavailable_people", "history_unavailable_people"].every(
      (key) => count(quality[key]) && quality[key] <= data.roster.cohort_total,
    ),
  );
  requireValid(
    quality.report_unavailable_people === data.coverage.unavailable &&
      Array.isArray(quality.issues),
  );
  if (quality.report_unavailable_people || quality.history_unavailable_people)
    requireValid(quality.state === "partial" && !quality.history_complete);
  if (quality.state === "complete")
    requireValid(quality.history_complete && quality.issues.length === 0);
  const checkMonths = (points) => {
    requireValid(Array.isArray(points) && points.length === 6);
    const [y, m] = data.as_of.date.split("-").map(Number);
    points.forEach((point, index) => {
      const month = new Date(Date.UTC(y, m - 6 + index, 1));
      const end = new Date(
        Date.UTC(month.getUTCFullYear(), month.getUTCMonth() + 1, 0),
      )
        .toISOString()
        .slice(0, 10);
      requireValid(
        point.month === month.toISOString().slice(0, 7) &&
          point.cutoff_date === (index === 5 ? data.as_of.date : end) &&
          point.partial === (index === 5),
      );
    });
  };
  requireValid(data.overall);
  checkCounts(data.overall.current);
  requireValid(data.overall.current.eligible_people === data.coverage.current);
  checkMovement(data.overall.since_last_test, data.coverage.current);
  checkMonths(data.overall.monthly_movement);
  data.overall.monthly_movement.forEach((row) =>
    requireValid(
      ["improved", "declined", "same", "compared_events"].every((key) =>
        count(row[key]),
      ) && row.compared_events === row.improved + row.declined + row.same,
    ),
  );
  requireValid(
    Array.isArray(data.care_areas) &&
      data.care_areas.map((area) => area.id).join(",") ===
        Object.keys(AREA_LABELS[mode]).join(","),
  );
  data.care_areas.forEach((area) => {
    checkCounts(area.current);
    requireValid(area.current.eligible_people === data.coverage.current);
    checkMovement(area.since_last_test, data.coverage.current);
    checkMonths(area.monthly_status);
    area.monthly_status.forEach((row) => {
      checkCounts(row.counts);
      requireValid(row.counts.eligible_people <= data.roster.cohort_total);
    });
    const delta = area.delta,
      previous = area.monthly_status[4];
    const reason = !data.quality.history_complete
      ? "incomplete_history"
      : !area.current.scored_people
        ? "no_current_scores"
        : !previous.counts.scored_people
          ? "no_previous_scores"
          : null;
    requireValid(
      delta &&
        delta.reason === reason &&
        delta.previous_cutoff_date === previous.cutoff_date,
    );
    if (reason === "incomplete_history")
      requireValid(
        delta.current_share_percent === null &&
          delta.previous_share_percent === null,
      );
    else {
      requireValid(
        area.current.high_share_percent === null
          ? delta.current_share_percent === null
          : near(delta.current_share_percent, area.current.high_share_percent),
      );
      requireValid(
        previous.counts.high_share_percent === null
          ? delta.previous_share_percent === null
          : near(
              delta.previous_share_percent,
              previous.counts.high_share_percent,
            ),
      );
      const last = area.monthly_status[5].counts;
      requireValid(
        [
          "high",
          "mid",
          "low",
          "no_data",
          "eligible_people",
          "scored_people",
        ].every((key) => last[key] === area.current[key]),
      );
    }
    if (reason) requireValid(delta.percentage_points === null);
    else
      requireValid(
        near(
          delta.percentage_points,
          (100 * area.current.high) / area.current.scored_people -
            (100 * previous.counts.high) / previous.counts.scored_people,
        ),
      );
  });
  return data;
}
